import { WOOTDASH_BASE, wootdashAuthHeaders } from '@/api/wootdash'

// Agendor (CRM) — plano-agendor.md. O atendimento só vê o Agendor com a integração ligada e o agente atribuído em
// Admin › Agendor; a configuração é settings.manage.

export interface AgendorAccess { allowed: boolean; enabled: boolean; assigned: boolean; reason: string | null }

export interface AgendorLink {
  contactId: string; personId: string; personName: string; organizationName: string | null; ownerName: string | null
  webUrl: string | null; origin: 'Bot' | 'Agent' | 'PhoneMatch'; syncedAt: string
}

export interface AgendorPerson { id: string; name: string; organizationName: string | null; ownerName: string | null; webUrl: string | null }

export type AgendorMatchOutcome = 'Linked' | 'AlreadyLinked' | 'NotFound' | 'Ambiguous' | 'NoPhone' | 'NotReady' | 'Failed'

export interface AgendorActionResult {
  allowed: boolean
  match: { outcome: AgendorMatchOutcome; link: AgendorLink | null; candidates: AgendorPerson[] | null; problem: string | null } | null
  problem: string | null
}

export interface AgendorUser { id: string; name: string; email: string | null }
export interface AgendorAgentRow {
  agentId: string; name: string; email: string; isActive: boolean; hasAccess: boolean
  agendorUserId: string | null; agendorUserName: string | null; suggestedUserId: string | null
}
export interface AgendorAgentsResult { agents: AgendorAgentRow[]; agendorUsers: AgendorUser[]; usersProblem: string | null }
export interface AgendorTestResult { ok: boolean; problem: string | null; userName: string | null; userEmail: string | null; linked: number; notFound: number }
export interface AgendorBatchResult { ok: boolean; problem: string | null; checked: number; linked: number; notFound: number; ambiguous: number; remaining: number }

const OFFLINE = 'Não foi possível falar com o servidor.'

async function call<T>(token: string, path: string, init: RequestInit = {}, fallback: T): Promise<T> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}${path}`, {
      ...init,
      headers: { ...wootdashAuthHeaders(token), ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
    })
    if (!res.ok) return fallback
    return await res.json()
  } catch {
    return fallback
  }
}

export function getAgendorAccess(token: string): Promise<AgendorAccess> {
  return call(token, '/agendor/acesso', {}, { allowed: false, enabled: false, assigned: false, reason: 'Não deu pra verificar o Agendor agora.' })
}

export function getAgendorLinks(token: string, contactIds: string[]): Promise<{ allowed: boolean; links: AgendorLink[] }> {
  return call(token, '/agendor/vinculos/consulta', { method: 'POST', body: JSON.stringify({ contatoIds: contactIds }) }, { allowed: false, links: [] })
}

const failed = (problem: string): AgendorActionResult => ({ allowed: true, match: null, problem })
const withConversation = (conversationId?: string | null) => (conversationId ? `?conversaId=${encodeURIComponent(conversationId)}` : '')

export function findAgendorContact(token: string, contactId: string, conversationId?: string | null): Promise<AgendorActionResult> {
  return call(token, `/agendor/contatos/${contactId}/procurar${withConversation(conversationId)}`, { method: 'POST' }, failed(OFFLINE))
}

export function linkAgendorPerson(token: string, contactId: string, personId: string, conversationId?: string | null): Promise<AgendorActionResult> {
  return call(token, `/agendor/contatos/${contactId}/vincular${withConversation(conversationId)}`,
    { method: 'POST', body: JSON.stringify({ pessoaId: personId }) }, failed(OFFLINE))
}

export function unlinkAgendorContact(token: string, contactId: string, conversationId?: string | null): Promise<AgendorActionResult> {
  return call(token, `/agendor/contatos/${contactId}/vinculo${withConversation(conversationId)}`, { method: 'DELETE' }, failed(OFFLINE))
}

// ── Admin › Agendor ──

export function testAgendor(token: string): Promise<AgendorTestResult> {
  return call(token, '/agendor/admin/testar', { method: 'POST' }, { ok: false, problem: OFFLINE, userName: null, userEmail: null, linked: 0, notFound: 0 })
}

export function listAgendorAgents(token: string): Promise<AgendorAgentsResult> {
  return call(token, '/agendor/admin/agentes', {}, { agents: [], agendorUsers: [], usersProblem: OFFLINE })
}

export async function setAgendorAgentAccess(token: string, agentId: string, hasAccess: boolean, user: AgendorUser | null): Promise<boolean> {
  const result = await call<{ success: boolean }>(token, `/agendor/admin/agentes/${agentId}`, {
    method: 'PUT',
    body: JSON.stringify({ temAcesso: hasAccess, usuarioAgendorId: user?.id ?? null, usuarioAgendorNome: user?.name ?? null }),
  }, { success: false })
  return result.success
}

export function matchAgendorByPhone(token: string, limit: number): Promise<AgendorBatchResult> {
  return call(token, '/agendor/admin/vincular-pelo-telefone', { method: 'POST', body: JSON.stringify({ limite: limit }) },
    { ok: false, problem: OFFLINE, checked: 0, linked: 0, notFound: 0, ambiguous: 0, remaining: 0 })
}

// ── Fase 4: painel e ações na conversa (cada ação devolve o painel atualizado) ──

export interface AgendorStage { id: string; name: string; sequence: number }
export interface AgendorFunnel { id: string; name: string; isDefault: boolean; stages: AgendorStage[] }
export interface AgendorLossReason { id: string; name: string }
export type AgendorDealStatus = 'ongoing' | 'won' | 'lost'
export interface AgendorDeal {
  id: string; title: string; funnelId: string | null; funnelName: string | null; stageSequence: number | null; stageName: string | null
  status: AgendorDealStatus; value: number | null; ownerName: string | null; webUrl: string | null; updatedAt: string | null
}
export interface AgendorTask { id: string; text: string; dueAt: string | null; type: string | null; assignedNames: string[]; webUrl: string | null }
export interface AgendorPanelUser { id: string; name: string; agentId: string | null }
export interface AgendorPanel {
  link: AgendorLink | null; deals: AgendorDeal[]; openTasks: AgendorTask[]; funnels: AgendorFunnel[]; lossReasons: AgendorLossReason[]
  users: AgendorPanelUser[]; myUserId: string | null; ownerAgentId: string | null; defaultFunnelId: string | null; defaultStageSequence: number | null
  problem: string | null
}
export interface AgendorPanelResult { allowed: boolean; ok: boolean; problem: string | null; panel: AgendorPanel | null }

export const AGENDOR_TASK_TYPES: Record<string, string> = {
  LIGACAO: 'Ligação', WHATSAPP: 'WhatsApp', EMAIL: 'E-mail', REUNIAO: 'Reunião', VISITA: 'Visita', PROPOSTA: 'Proposta',
}

const panelFailed = (problem: string): AgendorPanelResult => ({ allowed: true, ok: false, problem, panel: null })

export function getAgendorPanel(token: string, contactId: string, reload = false): Promise<AgendorPanelResult> {
  return call(token, `/agendor/contatos/${contactId}/painel${reload ? '?recarregar=true' : ''}`, {}, panelFailed(OFFLINE))
}

async function panelCall(token: string, path: string, method: string, body: unknown): Promise<AgendorPanelResult> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}${path}`, {
      method, headers: { ...wootdashAuthHeaders(token), 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    })
    if (res.status === 403) return panelFailed('Você não tem permissão pra isso no Agendor (peça em Admin › Perfis).')
    if (!res.ok) return panelFailed(`Erro ${res.status} no servidor.`)
    return await res.json()
  } catch {
    return panelFailed(OFFLINE)
  }
}

export function createAgendorPerson(token: string, contactId: string, conversationId: string | null,
  person: { nome: string; email?: string | null; empresa?: string | null; cnpj?: string | null; responsavelId?: string | null }): Promise<AgendorPanelResult> {
  return panelCall(token, `/agendor/contatos/${contactId}/pessoa${withConversation(conversationId)}`, 'POST', person)
}

export function createAgendorDeal(token: string, contactId: string, conversationId: string | null,
  deal: { titulo: string; funilId: string; etapa: number; valor?: number | null }): Promise<AgendorPanelResult> {
  return panelCall(token, `/agendor/contatos/${contactId}/negocios${withConversation(conversationId)}`, 'POST', deal)
}

export function moveAgendorDeal(token: string, contactId: string, conversationId: string | null, dealId: string, funnelId: string, stage: number): Promise<AgendorPanelResult> {
  return panelCall(token, `/agendor/contatos/${contactId}/negocios/${encodeURIComponent(dealId)}/etapa${withConversation(conversationId)}`, 'PUT', { funilId: funnelId, etapa: stage })
}

export function setAgendorDealStatus(token: string, contactId: string, conversationId: string | null, dealId: string,
  status: AgendorDealStatus, lossReasonId?: string | null): Promise<AgendorPanelResult> {
  return panelCall(token, `/agendor/contatos/${contactId}/negocios/${encodeURIComponent(dealId)}/situacao${withConversation(conversationId)}`, 'PUT',
    { situacao: status, motivoPerdaId: lossReasonId ?? null })
}

export function createAgendorTask(token: string, contactId: string, conversationId: string | null,
  task: { texto: string; vencimento?: string | null; tipo?: string | null; responsavelId?: string | null }): Promise<AgendorPanelResult> {
  return panelCall(token, `/agendor/contatos/${contactId}/tarefas${withConversation(conversationId)}`, 'POST', task)
}

/** Sem usuário = assumir (o usuário do agente no Agendor). */
export function setAgendorOwner(token: string, contactId: string, conversationId: string | null, agendorUserId: string | null): Promise<AgendorPanelResult> {
  return panelCall(token, `/agendor/contatos/${contactId}/responsavel${withConversation(conversationId)}`, 'PUT', { usuarioAgendorId: agendorUserId })
}

export function listAgendorFunnels(token: string, reload = false): Promise<{ ok: boolean; problem: string | null; funnels: AgendorFunnel[]; lossReasons: AgendorLossReason[] }> {
  return call(token, `/agendor/admin/funis${reload ? '?recarregar=true' : ''}`, {}, { ok: false, problem: OFFLINE, funnels: [], lossReasons: [] })
}
